'use client';

import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Hex } from 'viem';
import { Coins, Gem, CheckCheck, Loader2, Landmark } from 'lucide-react';
import { useGrids } from '@takeover/sdk/client';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { BoardCover, boardInitials } from '../ui/BoardCover';
import { formatCompactUsd } from '../data/mock-prices';
import { useLobbyHeaderStats, type LobbyClaimableGrid } from '@/hooks/use-lobby-header-stats';
import { useWallet } from '@/hooks/use-wallet';
import { useTransactionQueue } from '@/hooks/use-transactions';
import { queryKeys } from '@/lib/query-keys';
import {
  buildSeatFeeClaimSteps,
  buildSingleGridFeeClaimStep,
  buildSingleGridTaxWithdrawStep,
} from '@/lib/claim-seat-fees-steps';
import { fetchPortfolioJson } from '@/lib/fetchers/portfolio-json';
import { playClaimFeesSound } from '@/lib/sounds';
import type { PendingFeesCachedShape } from '@/lib/fetchers/pending-fees-query';

interface RewardsSheetProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

interface ClaimableRow {
  /** Stable identifier for `claimed` tracking. */
  key: string;
  /** Display name shown next to the cover. */
  name: string;
  /** USD claimable amount. */
  amount: number;
  /** Original SDK row (for icons / symbols / claim kind). */
  live: LobbyClaimableGrid;
}

export function RewardsSheet({ open, onOpenChange }: RewardsSheetProps) {
  const [claimingAll, setClaimingAll] = useState(false);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const liveStats = useLobbyHeaderStats();
  const { address } = useWallet();
  const { executeActions } = useTransactionQueue();
  const queryClient = useQueryClient();

  // Piggybacks on the lobby's own grid query (same cache key) so the
  // rewards sheet renders each row's actual board cover from
  // `pool_metadata.imageUrl` before falling back to the procedural
  // cover — users should never see the generated mosaic when a real
  // metadata image is on file.
  const { data: liveGrids = [] } = useGrids({ curation: 'all', limit: 100 });
  const boardImageByGridId = useMemo(() => {
    const m = new Map<string, string>();
    for (const g of liveGrids) {
      if (g.imageUrl) m.set(g.id, g.imageUrl);
    }
    return m;
  }, [liveGrids]);

  const rows = useMemo<ClaimableRow[]>(() => {
    if (!liveStats.isLive) return [];
    return liveStats.claimableGrids.map((g) => ({
      key: `${g.claimKind}:${g.gridId}`,
      name: g.gridName,
      amount: g.usdValue,
      live: g,
    }));
  }, [liveStats.isLive, liveStats.claimableGrids]);

  const totalUsd = rows.reduce((a, r) => a + r.amount, 0);
  const yieldUsd = rows
    .filter((r) => r.live.claimKind === 'tile_yield')
    .reduce((a, r) => a + r.amount, 0);
  const taxUsd = rows
    .filter((r) => r.live.claimKind === 'board_tax')
    .reduce((a, r) => a + r.amount, 0);
  const hasYield = rows.some((r) => r.live.claimKind === 'tile_yield');
  const hasTax = rows.some((r) => r.live.claimKind === 'board_tax');

  /**
   * Optimistically zero the just-claimed rows in the cached `pendingFees`
   * payload so the WalletActionButton on the mobile profile (and the
   * desktop nav rewards pill) drops to "None" / the row disappears in
   * the same paint as the success snackbar — without waiting for a
   * round-trip through `/api/rpc/pending-fees/[address]`.
   *
   * Why this is needed: the API route sits behind a 15 s TTL + 30 s
   * SWR server cache (see `userPendingFees` in `lib/cache/rpc-cache.ts`).
   * A vanilla `invalidateQueries` triggers an immediate client refetch
   * but the server still serves the pre-claim payload — so the chip
   * displays a stale balance until the next refetch lands after the
   * cache expires (or the user hard-refreshes). The optimistic write
   * here lets the UI reflect the claim instantly; `refreshFromServer`
   * below then drops the server cache via `?fresh=1` and re-invalidates
   * the client cache so on-chain truth replaces the stub on the next
   * tick.
   */
  const applyClaimedRowsOptimistically = useCallback(
    (claimed: ReadonlyArray<{ gridId: string; claimKind: LobbyClaimableGrid['claimKind'] }>) => {
      if (!address || claimed.length === 0) return;
      const claimedYield = new Set(
        claimed.filter((c) => c.claimKind === 'tile_yield').map((c) => c.gridId),
      );
      const claimedTax = new Set(
        claimed.filter((c) => c.claimKind === 'board_tax').map((c) => c.gridId),
      );
      queryClient.setQueryData<PendingFeesCachedShape | null | undefined>(
        queryKeys.pendingFees(address),
        (prev) => {
          if (!prev) return prev;
          let nextV2Breakdown = prev.v2Breakdown;
          if (claimedYield.size > 0) {
            nextV2Breakdown = prev.v2Breakdown.map((g) =>
              claimedYield.has(g.gridId)
                ? { ...g, rawPendingFees: '0', pendingFees: '0', usdValue: 0 }
                : g,
            );
          }
          let nextTaxBreakdown = prev.creatorTaxBreakdown;
          if (claimedTax.size > 0) {
            nextTaxBreakdown = prev.creatorTaxBreakdown.map((t) =>
              claimedTax.has(t.gridId)
                ? { ...t, taxRevenue: '0', usdValue: 0 }
                : t,
            );
          }
          // Recompute aggregates so `useLobbyHeaderStats`'s `displayUsd`
          // / `hasAnyClaimable` derivation flips with the same write —
          // otherwise the chip's loading skeleton lingers until the
          // next refetch.
          const remainingV2 = nextV2Breakdown.some((g) => {
            try { return BigInt(g.rawPendingFees ?? g.pendingFees ?? '0') > 0n; } catch { return false; }
          });
          const remainingTax = nextTaxBreakdown.some((t) => {
            try { return BigInt(t.taxRevenue ?? '0') > 0n; } catch { return false; }
          });
          return {
            ...prev,
            v2Breakdown: nextV2Breakdown,
            creatorTaxBreakdown: nextTaxBreakdown,
            v2GridIds: nextV2Breakdown
              .filter((g) => {
                try { return BigInt(g.rawPendingFees ?? g.pendingFees ?? '0') > 0n; } catch { return false; }
              })
              .map((g) => g.gridId),
            hasAnyClaimable: remainingV2 || remainingTax,
          };
        },
      );
    },
    [address, queryClient],
  );

  /**
   * Drop the server-side `userPendingFees` + `usdcBalance` cache
   * entries, then re-invalidate the client query cache with
   * `refetchType: 'all'` so even inactive observers refetch when
   * next mounted. Mirrors the heavy-handed reset that
   * `refreshAfterTransaction` already applies for seat claims —
   * without it, the fee-claim flow re-fetches the still-warm server
   * cache and shows pre-claim totals until the TTL expires.
   *
   * Both `?fresh=1` calls fire in parallel: balance is a single RPC
   * read so it's cheap regardless, and pending-fees needs explicit
   * invalidation here because the balance route's `?fresh=1` no
   * longer wipes it (closing the foot-gun where every user-initiated
   * "refresh balance" tap triggered the heaviest envio fanout in
   * the app — see `/api/rpc/balance/[address]/route.ts`).
   */
  const refreshFromServer = useCallback(async () => {
    if (!address) return;
    try {
      await Promise.all([
        fetch(`/api/rpc/balance/${address}?fresh=1`),
        fetch(`/api/rpc/pending-fees/${address}?fresh=1`),
      ]);
    } catch {
      // Network blips here are non-fatal — the optimistic update has
      // already moved the UI; the next periodic refetch will reconcile
      // against on-chain truth.
    }
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: queryKeys.pendingFees(address),
        refetchType: 'all',
      }),
      queryClient.invalidateQueries({
        queryKey: queryKeys.portfolio(address),
        refetchType: 'all',
      }),
    ]);
  }, [address, queryClient]);

  // "Your gold has arrived." Reverted to the legacy `playClaimFeesSound()`
  // (the cash-register drawer-slide + double-ring from `lib/sounds.ts`) to
  // match `classic.takeover.fun` — the V1 `rewards-drawer.tsx` still uses
  // the same function on its claim-success path, so both flows now feel
  // identical. Yield and tax claims here represent *aggregated rewards
  // landing in the wallet* rather than a single tile being claimed, which
  // is exactly the semantic the legacy cha-ching cue was designed around.
  const onClaimSuccess = useCallback(
    async (claimed: ReadonlyArray<{ gridId: string; claimKind: LobbyClaimableGrid['claimKind'] }>) => {
      applyClaimedRowsOptimistically(claimed);
      playClaimFeesSound();
      await refreshFromServer();
    },
    [applyClaimedRowsOptimistically, refreshFromServer],
  );

  const handleClaimRow = useCallback(
    async (row: ClaimableRow) => {
      console.info('[RewardsSheet] handleClaimRow click', {
        rowKey: row.key,
        gridId: row.live.gridId,
        claimKind: row.live.claimKind,
        live: liveStats.isLive,
        hasAddress: !!address,
        hasPendingFees: !!liveStats.pendingFees,
      });
      if (!liveStats.isLive || !address || !liveStats.pendingFees || !row.live.gridId) {
        console.warn('[RewardsSheet] handleClaimRow: preconditions not met — bailing out');
        return;
      }
      const pf = liveStats.pendingFees;
      setClaimingId(row.key);
      // Snapshot the row's identity so the success callback's
      // optimistic update zeros exactly the slot the user just claimed.
      const claimedContext = [
        { gridId: row.live.gridId, claimKind: row.live.claimKind },
      ] as const;
      try {
        if (row.live.claimKind === 'board_tax') {
          const step = buildSingleGridTaxWithdrawStep(row.live.gridId, pf.creatorTaxBreakdown);
          if (!step) {
            console.warn('[RewardsSheet] handleClaimRow: tax withdraw step null — nothing to claim');
            return;
          }
          await executeActions(step.label, [step], {
            onSuccess: () => { void onClaimSuccess(claimedContext); },
          });
          return;
        }
        const step = await buildSingleGridFeeClaimStep(row.live.gridId, pf.v2Breakdown);
        if (!step) {
          console.warn('[RewardsSheet] handleClaimRow: fee claim step null — nothing to claim');
          return;
        }
        await executeActions(step.label, [step], {
          onSuccess: () => { void onClaimSuccess(claimedContext); },
        });
      } catch (e) {
        console.error('[RewardsSheet] claim row failed:', e);
      } finally {
        setClaimingId(null);
      }
    },
    [address, executeActions, onClaimSuccess, liveStats.isLive, liveStats.pendingFees],
  );

  const handleClaimAll = useCallback(async () => {
    console.info('[RewardsSheet] handleClaimAll click', {
      live: liveStats.isLive,
      hasAddress: !!address,
      hasPendingFees: !!liveStats.pendingFees,
      hasAnyClaimable: liveStats.hasAnyClaimable,
    });
    if (!liveStats.isLive || !address || !liveStats.pendingFees || !liveStats.hasAnyClaimable) {
      console.warn('[RewardsSheet] handleClaimAll: preconditions not met — bailing out');
      return;
    }
    setClaimingAll(true);
    // Snapshot every claimable row so the success callback's optimistic
    // update zeros the entire visible list — `rows` is a stable derived
    // value of the current `pendingFees` cache and matches what the
    // executor will actually settle on-chain.
    const claimedContext = rows.map((r) => ({
      gridId: r.live.gridId,
      claimKind: r.live.claimKind,
    }));
    try {
      const json = await fetchPortfolioJson(address);
      const seats = (json as { data?: { seats?: unknown[] } })?.data?.seats ?? [];
      const steps = await buildSeatFeeClaimSteps(
        address as Hex,
        liveStats.pendingFees,
        seats as { pool?: { managerAddress?: string; graduationManagerAddress?: string } }[],
      );
      console.info('[RewardsSheet] handleClaimAll built steps', { stepCount: steps.length });
      if (steps.length === 0) {
        console.warn('[RewardsSheet] handleClaimAll: zero steps built — nothing to claim');
        return;
      }
      const title =
        totalUsd >= 0.01 ? `Claim ${formatCompactUsd(totalUsd)}` : 'Claim rewards';
      await executeActions(title, steps, {
        onSuccess: () => { void onClaimSuccess(claimedContext); },
      });
    } catch (e) {
      console.error('[RewardsSheet] claim all failed:', e);
    } finally {
      setClaimingAll(false);
    }
  }, [
    address,
    executeActions,
    onClaimSuccess,
    liveStats.hasAnyClaimable,
    liveStats.isLive,
    liveStats.pendingFees,
    rows,
    totalUsd,
  ]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          'w-[380px] max-w-[92vw] p-0 h-full max-h-dvh min-h-0 flex flex-col overflow-hidden',
          'bg-[#0b0a08]/95 backdrop-blur-md border-l border-amber-500/30 text-zinc-100',
        )}
      >
        <SheetHeader className="shrink-0 px-5 py-3 border-b border-amber-500/20">
          <SheetTitle className="text-amber-100 flex items-center gap-2">
            <Gem className="h-4 w-4" />
            Rewards
          </SheetTitle>
          <SheetDescription className="text-zinc-500 text-[11px] font-mono tracking-[0.2em] uppercase">
            Claim accrued yield per grid · {rows.length} with balance
          </SheetDescription>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 px-5 py-3 border-b border-amber-500/10 bg-gradient-to-b from-amber-500/5 to-transparent">
            <div className="text-[9px] font-mono tracking-[0.22em] uppercase text-zinc-500">
              Total claimable
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-amber-100 tabular-nums">
                {formatCompactUsd(totalUsd)}
              </span>
            </div>
            {(hasYield || hasTax) && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {hasYield && (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[3px]',
                      'text-[9px] font-mono uppercase tracking-[0.18em]',
                      'bg-amber-500/10 border border-amber-400/30 text-amber-200',
                    )}
                  >
                    <Gem className="h-2.5 w-2.5" />
                    Yield · {formatCompactUsd(yieldUsd)}
                  </span>
                )}
                {hasTax && (
                  <span
                    className={cn(
                      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[3px]',
                      'text-[9px] font-mono uppercase tracking-[0.18em]',
                      'bg-emerald-500/10 border border-emerald-400/30 text-emerald-200',
                    )}
                  >
                    <Landmark className="h-2.5 w-2.5" />
                    Tax · {formatCompactUsd(taxUsd)}
                  </span>
                )}
              </div>
            )}
          </div>

          <div data-gild-scroll className="min-h-0 flex-1 overflow-y-auto">
            {rows.map((row) => (
              <ClaimRow
                key={row.key}
                row={row}
                live={liveStats.isLive}
                claiming={claimingId === row.key}
                boardImageUrl={boardImageByGridId.get(row.live.gridId) ?? null}
                onClaim={() => void handleClaimRow(row)}
              />
            ))}
            {rows.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-2 py-8 text-center text-zinc-500">
                <CheckCheck className="h-5 w-5 text-emerald-400/80" />
                <span className="text-[11px] font-mono tracking-[0.2em] uppercase">
                  Nothing claimable yet
                </span>
                <span className="text-[10px] text-zinc-600 normal-case tracking-normal max-w-[220px]">
                  Pending fees show up here as your owned tiles accrue yield.
                </span>
              </div>
            )}
          </div>

          <div className="shrink-0 border-t border-amber-500/20 p-3 bg-black/50">
            <button
              type="button"
              onClick={() => void handleClaimAll()}
              disabled={
                rows.length === 0 ||
                claimingAll ||
                claimingId !== null ||
                !liveStats.isLive ||
                !liveStats.hasAnyClaimable
              }
              className={cn(
                'w-full h-10 rounded-[4px] border text-[11px] font-semibold uppercase tracking-[0.2em] flex items-center justify-center gap-2 transition-colors',
                rows.length === 0 || claimingAll || !liveStats.isLive || !liveStats.hasAnyClaimable
                  ? 'bg-white/5 border-white/10 text-zinc-500 cursor-not-allowed'
                  : 'bg-gradient-to-b from-amber-400 to-amber-500 border-amber-300/60 text-black hover:brightness-110',
              )}
            >
              {claimingAll ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Coins className="h-3.5 w-3.5" />
              )}
              {claimingAll
                ? 'Claiming…'
                : rows.length === 0 || !liveStats.hasAnyClaimable
                  ? 'Nothing to claim'
                  : `Claim all · ${formatCompactUsd(totalUsd)}`}
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface ClaimRowProps {
  row: ClaimableRow;
  live: boolean;
  claiming: boolean;
  /** Preferred cover image from live lobby grid metadata — takes priority over
   *  the pending-fees `gridImageUrl`, which can fall through to the generic
   *  `/api/token-icon/…` proxy when a board has no proper metadata image. */
  boardImageUrl: string | null;
  onClaim: () => void;
}

/**
 * Per-kind visual treatment for the rewards list. `tile_yield` keeps the
 * app's amber/gold language ("rewards you farm as a holder"), while
 * `board_tax` uses emerald ("creator revenue the board collected for
 * you"). Making the two kinds visually distinct avoids users mistaking
 * a one-off tax withdraw for their regular claimable yield.
 */
interface KindStyle {
  accent: string;        // left edge strip
  badge: string;         // kind label pill
  amount: string;        // dollar value text
  button: string;        // claim CTA
  BadgeIcon: typeof Gem;
  kindLabel: string;
  rowHover: string;
}

const KIND_STYLES: Record<LobbyClaimableGrid['claimKind'], KindStyle> = {
  tile_yield: {
    accent: 'bg-gradient-to-b from-amber-400/70 to-amber-600/40',
    badge:
      'bg-amber-500/15 border-amber-400/40 text-amber-100',
    amount: 'text-amber-100',
    button:
      'bg-amber-500/10 border-amber-400/40 text-amber-100 hover:bg-amber-500/25',
    BadgeIcon: Gem,
    kindLabel: 'Tile yield',
    rowHover: 'hover:bg-amber-500/[0.04]',
  },
  board_tax: {
    accent: 'bg-gradient-to-b from-emerald-400/70 to-emerald-600/40',
    badge:
      'bg-emerald-500/15 border-emerald-400/40 text-emerald-100',
    amount: 'text-emerald-100',
    button:
      'bg-emerald-500/10 border-emerald-400/40 text-emerald-100 hover:bg-emerald-500/25',
    BadgeIcon: Landmark,
    kindLabel: 'Board tax',
    rowHover: 'hover:bg-emerald-500/[0.04]',
  },
};

/**
 * `/api/token-icon/:address` is the last-resort coin-logo proxy the
 * image resolver falls back to when a board has no pool_metadata /
 * Envio / xref image on file. It returns the memecoin's CoinGecko
 * icon (or a transparent PNG if even that fails) — which looks
 * unrelated to the board itself in the rewards sheet. Treat those
 * URLs as "no image" here so the `BoardCover` procedural mosaic
 * shows instead, per the product direction of "board metadata
 * only, generated cover as the fallback".
 */
function preferRealBoardImage(
  ...urls: (string | null | undefined)[]
): string | null {
  for (const u of urls) {
    if (u && !u.startsWith('/api/token-icon/')) return u;
  }
  return null;
}

function ClaimRow({ row, live, claiming, boardImageUrl, onClaim }: ClaimRowProps) {
  const seed = row.live.gridId || row.key;
  const hue = hashHue(seed);
  const initials = boardInitials(row.name);
  // Image priority: live lobby metadata → pending-fees resolver →
  // procedural mosaic. We skip the `/api/token-icon/` proxy entirely
  // so users never see a random CoinGecko coin logo in place of the
  // board's actual cover image.
  const imageUrl = preferRealBoardImage(boardImageUrl, row.live.gridImageUrl);
  const canClaimRow = live && !!row.live.gridId;
  const tokenLabel = row.live.tokenSymbol ?? row.live.yieldTokenSymbol;
  const style = KIND_STYLES[row.live.claimKind];
  const { BadgeIcon } = style;

  return (
    <div
      className={cn(
        'relative flex items-center gap-3 px-5 py-3 border-b border-white/[0.04] transition-colors',
        style.rowHover,
      )}
    >
      {/* Left-edge accent strip — the fastest visual signal that tells
          reward (amber) and tax (emerald) rows apart at a glance. */}
      <span
        aria-hidden
        className={cn('absolute left-0 top-2 bottom-2 w-[2px] rounded-r-full', style.accent)}
      />
      <BoardCover
        seed={seed}
        hue={hue}
        initials={initials}
        imageUrl={imageUrl}
        size={40}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="text-[12px] font-semibold text-amber-100 truncate">{row.name}</div>
          <span
            className={cn(
              'shrink-0 inline-flex items-center gap-1 text-[8px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-[3px] border',
              style.badge,
            )}
          >
            <BadgeIcon className="h-2.5 w-2.5" />
            {style.kindLabel}
          </span>
        </div>
        <div className="mt-0.5 flex items-baseline gap-1.5">
          <span className={cn('text-[13px] font-mono tabular-nums', style.amount)}>
            {formatCompactUsd(row.amount)}
          </span>
          {tokenLabel && (
            <span className="text-[9px] font-mono tracking-[0.18em] uppercase text-zinc-500">
              {tokenLabel}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={onClaim}
        disabled={!canClaimRow || claiming}
        title={!live ? 'Connect wallet with live pending fees' : undefined}
        className={cn(
          'h-7 px-3 rounded-[3px] border text-[10px] font-mono tracking-[0.18em] uppercase transition-colors shrink-0 inline-flex items-center justify-center gap-1',
          !canClaimRow
            ? 'bg-white/5 border-white/10 text-zinc-500 cursor-not-allowed'
            : style.button,
        )}
      >
        {claiming ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
        {canClaimRow ? 'Claim' : !live ? 'Demo' : '—'}
      </button>
    </div>
  );
}

/**
 * Deterministic hex hue for a board's cover fallback. `BoardCover`
 * concatenates an alpha suffix (`${hue}66`) for its text-shadow, so
 * the return shape has to be a 6-char hex string — HSL won't fly.
 * Matches the logic used in `features/lobby/data/grid-to-board.ts`.
 */
function hashHue(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const hueDeg = h % 360;
  return hslToHex(hueDeg, 70, 55);
}

function hslToHex(h: number, s: number, l: number): string {
  const sNorm = s / 100;
  const lNorm = l / 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = sNorm * Math.min(lNorm, 1 - lNorm);
  const f = (n: number) => {
    const v = lNorm - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
    return Math.round(v * 255);
  };
  const toHex = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
}

/**
 * Zero-totals fallback for the header rewards badge while
 * `useLobbyHeaderStats()` resolves. Kept as a function (instead of a
 * literal) so every badge consumer reads through the same API surface
 * and can be upgraded to pre-hydrated session data later without a
 * call-site churn.
 */
export function getClaimableTotals() {
  return { total: 0, count: 0, symbol: 'USDC' };
}
